import { useState, useCallback } from 'react';
import { entradasMensualesAPI } from '../services/entradasMensualesAPI';
import { validarEntrada } from '../utils/validacionGastos';

export const useEntradasMensuales = ({ onToast } = {}) => {
  const [entradas, setEntradas] = useState([]);
  const [totalMensual, setTotalMensual] = useState(0);
  const [sumaAcumulada, setSumaAcumulada] = useState(0);
  const [centroSeleccionado, setCentroSeleccionado] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const buscar = useCallback(async (mes, anio, idCentro) => {
    setLoading(true);
    setError(null);

    try {
      const data = await entradasMensualesAPI.getEntradas(mes, anio, idCentro);

      setEntradas(data.items || []);
      setTotalMensual(data.totalMensual || 0);
      setSumaAcumulada(data.sumaAcumulada || 0);
    } catch (err) {
      console.error('Error al cargar entradas mensuales:', err);
      const mensaje = 'Error al cargar las entradas mensuales';
      setError(mensaje);
      if (onToast) {
        onToast({ type: 'error', message: mensaje });
      }
    } finally {
      setLoading(false);
    }
  }, [onToast]);

  const crear = useCallback(async (data) => {
    // Validar antes de enviar
    const validacion = validarEntrada(data);
    if (!validacion.valid) {
      return validacion;
    }

    setSaving(true);
    setError(null);

    try {
      const entradaCreada = await entradasMensualesAPI.crearEntrada(data);

      // Agregar la nueva entrada a la lista
      setEntradas(prev => [...prev, entradaCreada]);

      // Actualizar totales
      const monto = Number(entradaCreada.monto);
      setTotalMensual(prev => Math.round((prev + monto) * 100) / 100);
      setSumaAcumulada(prev => Math.round((prev + monto) * 100) / 100);

      if (onToast) {
        onToast({ type: 'success', message: 'Entrada creada correctamente' });
      }

      return { valid: true };
    } catch (err) {
      console.error('Error al crear entrada:', err);
      const mensaje = 'Error al crear la entrada';
      setError(mensaje);
      if (onToast) {
        onToast({ type: 'error', message: mensaje });
      }
      return { valid: false, error: 'save_failed' };
    } finally {
      setSaving(false);
    }
  }, [onToast]);

  const actualizar = useCallback(async (id, data) => {
    // Validar antes de enviar
    const validacion = validarEntrada(data);
    if (!validacion.valid) {
      return validacion;
    }

    setSaving(true);
    setError(null);

    try {
      const entradaActualizada = await entradasMensualesAPI.actualizarEntrada(id, data);

      // Encontrar la entrada anterior para calcular el delta
      setEntradas(prev => {
        const entradaAnterior = prev.find(e => e.id === id);
        const montoAnterior = entradaAnterior ? Number(entradaAnterior.monto) : 0;
        const montoNuevo = Number(entradaActualizada.monto);
        const delta = montoNuevo - montoAnterior;

        // Recalcular totalMensual desde la lista actualizada
        const nuevasEntradas = prev.map(e =>
          e.id === id ? entradaActualizada : e
        );
        const nuevoTotal = nuevasEntradas.reduce(
          (sum, e) => sum + Number(e.monto), 0
        );
        setTotalMensual(Math.round(nuevoTotal * 100) / 100);

        // Actualizar suma acumulada con el delta
        setSumaAcumulada(prevAcum => Math.round((prevAcum + delta) * 100) / 100);

        return nuevasEntradas;
      });

      if (onToast) {
        onToast({ type: 'success', message: 'Entrada actualizada correctamente' });
      }

      return { valid: true };
    } catch (err) {
      console.error('Error al actualizar entrada:', err);
      const mensaje = 'Error al actualizar la entrada';
      setError(mensaje);
      if (onToast) {
        onToast({ type: 'error', message: mensaje });
      }
      return { valid: false, error: 'save_failed' };
    } finally {
      setSaving(false);
    }
  }, [onToast]);

  const eliminar = useCallback(async (id) => {
    setSaving(true);
    setError(null);

    try {
      await entradasMensualesAPI.eliminarEntrada(id);

      // Encontrar el monto de la entrada eliminada y removerla
      setEntradas(prev => {
        const entradaEliminada = prev.find(e => e.id === id);
        const monto = entradaEliminada ? Number(entradaEliminada.monto) : 0;

        // Recalcular totales
        const nuevasEntradas = prev.filter(e => e.id !== id);
        const nuevoTotal = nuevasEntradas.reduce(
          (sum, e) => sum + Number(e.monto), 0
        );
        setTotalMensual(Math.round(nuevoTotal * 100) / 100);
        setSumaAcumulada(prevAcum => Math.round((prevAcum - monto) * 100) / 100);

        return nuevasEntradas;
      });

      if (onToast) {
        onToast({ type: 'success', message: 'Entrada eliminada correctamente' });
      }

      return { success: true };
    } catch (err) {
      console.error('Error al eliminar entrada:', err);
      const mensaje = 'Error al eliminar la entrada';
      setError(mensaje);
      if (onToast) {
        onToast({ type: 'error', message: mensaje });
      }
      return { success: false, error: 'delete_failed' };
    } finally {
      setSaving(false);
    }
  }, [onToast]);

  return {
    entradas,
    totalMensual,
    sumaAcumulada,
    centroSeleccionado,
    setCentroSeleccionado,
    loading,
    saving,
    error,
    buscar,
    crear,
    actualizar,
    eliminar,
  };
};
